import * as React from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { TwiddleOpenContextType, openContextDefaultValue } from '../components/NewTableTwiddle/contexts';
import { VirtualizerOptions } from './types';
import { densityHeightMap, useVirtualCallbacks, useFlatDataArray } from './utils';

/**
 * A hook that returns a virtualizer object for use with NewTable components.
 * @param data The data to be rendered in the table.
 * @param virtualizerOptions Options to customize the virtualizer.
 * @returns A virtualizer object with functions to be spread in NewTable components.
 */
const useWindowTableVirtualizer = <DataItem>(
  data: DataItem[],
  virtualizerOptions?: VirtualizerOptions<DataItem, Window>,
) => {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const [twiddleState, setTwiddleState] = React.useState<TwiddleOpenContextType>(openContextDefaultValue);

  const {
    count, //            count, estimateSize, and getScrollElement are unpacked here
    estimateSize, //     to avoid them getting overriden by the user, as they're
    getScrollElement, // provided by us.
    density = 'normal',
    overscan = 5,
    getItemKey,
    getTwiddleRowCount,
    ...options
  } = virtualizerOptions || {};

  const flatData = useFlatDataArray({
    data,
    rowsState: twiddleState,
    getTwiddleRowCount,
    getItemKey,
  });

  const virtualizer = useWindowVirtualizer<HTMLTableRowElement>({
    ...options,
    count: flatData.length,
    overscan,
    estimateSize: React.useCallback(() => densityHeightMap[density], [density]),
    scrollMargin: parentRef.current?.offsetTop ?? 0,
  });

  return useVirtualCallbacks({ virtualizer, flatData, parentRef, data, setTwiddleState, type: 'window' });
};

export default useWindowTableVirtualizer;
